import firebase from 'firebase';
import { usagePath } from 'pages/dashboard/Usage/pageTitle';

const FLUSH_MS = 60000;
const TICK_MS = 10000;
const IDLE_MS = 60000;
const MAX_BATCH = 25;
const MAX_QUEUE = 50;
const TOKEN_TTL_MS = 50 * 60 * 1000;

const baseURL = process.env.REACT_APP_NODE_ENV === 'development'
  ? 'http://localhost:3000'
  : 'https://api.gpmatrix.co.nz';

let queue = [];
let activeSeconds = 0;
let lastInput = 0;
let lastTick = 0;
let token = null;
let tokenAt = 0;
let started = false;
let flushTimer = null;
let tickTimer = null;
let lastPageKey = null;

const sessionId = () => {
  const key = 'usage.session';
  let id = sessionStorage.getItem(key);
  if (!id) {
    const uuid = window.crypto && window.crypto.randomUUID && window.crypto.randomUUID();
    id = uuid || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    sessionStorage.setItem(key, id);
  }
  return id;
};

export const pagePath = (pathname, search) => usagePath(pathname, search);

const livePath = () => pagePath(window.location.pathname, window.location.search);

const refreshToken = () => {
  const user = firebase.auth().currentUser;
  if (!user) return;
  user.getIdToken().then((next) => {
    token = next;
    tokenAt = Date.now();
  }).catch(() => {});
};

const accrue = () => {
  const now = Date.now();
  const elapsed = Math.round((now - lastTick) / 1000);
  lastTick = now;
  const visible = document.visibilityState === 'visible';
  const recentlyActive = lastInput && now - lastInput < IDLE_MS;
  if (visible && recentlyActive && elapsed > 0) {
    activeSeconds += Math.min(elapsed, 30);
  }
};

const send = (payload) => {
  fetch(`${baseURL}/usage/events`, {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      AuthToken: token,
    },
    body: JSON.stringify(payload),
  }).catch(() => {});
};

const flush = () => {
  accrue();
  if (!queue.length && activeSeconds === 0) return;
  const events = queue.splice(0, 50);
  const seconds = Math.min(activeSeconds, 300);
  activeSeconds = 0;
  if (!token) {
    queue = [...events, ...queue].slice(0, MAX_QUEUE);
    activeSeconds = Math.min(seconds, 300);
    refreshToken();
    return;
  }
  if (Date.now() - tokenAt > TOKEN_TTL_MS) refreshToken();
  send({
    events,
    activeSeconds: seconds,
    sessionId: sessionId(),
    path: livePath(),
    touch: navigator.maxTouchPoints > 1,
  });
};

const track = (type, fields) => {
  queue.push({
    type,
    path: fields.path || livePath(),
    label: fields.label || null,
    target: fields.target || null,
    sessionId: sessionId(),
  });
  if (queue.length >= MAX_BATCH) flush();
};

export const trackPage = (path) => {
  if (!path || path === lastPageKey) return;
  lastPageKey = path;
  track('page_view', { path });
};

const labelOf = (el) => {
  const raw = el.getAttribute('data-usage')
    || el.getAttribute('aria-label')
    || el.innerText
    || '';
  return raw.replace(/\s+/g, ' ').trim().slice(0, 80);
};

const onClick = (event) => {
  const el = event.target && event.target.closest
    && event.target.closest('button, a, [role="tab"], [role="menuitem"], .ant-menu-item');
  if (!el) return;
  const label = labelOf(el);
  if (!label) return;
  if (el.tagName === 'A' && /^https?:/i.test(el.href)) {
    const url = new URL(el.href);
    if (url.host && url.host !== window.location.host) {
      track('link', { path: livePath(), label, target: url.href.slice(0, 500) });
      return;
    }
  }
  track('click', { path: livePath(), label });
};

const markInput = () => {
  lastInput = Date.now();
};

const onVisibility = () => {
  if (document.visibilityState === 'hidden') flush();
};

const inputEvents = ['pointerdown', 'keydown', 'scroll', 'touchstart'];

export const start = () => {
  if (started) return;
  started = true;
  lastTick = Date.now();
  refreshToken();
  inputEvents.forEach((name) => {
    window.addEventListener(name, markInput, { passive: true, capture: true });
  });
  document.addEventListener('click', onClick, true);
  document.addEventListener('visibilitychange', onVisibility);
  tickTimer = window.setInterval(accrue, TICK_MS);
  flushTimer = window.setInterval(flush, FLUSH_MS);
};

export const stop = () => {
  if (!started) return;
  flush();
  started = false;
  window.clearInterval(tickTimer);
  window.clearInterval(flushTimer);
  inputEvents.forEach((name) => {
    window.removeEventListener(name, markInput, true);
  });
  document.removeEventListener('click', onClick, true);
  document.removeEventListener('visibilitychange', onVisibility);
};
